import type {
  CloneQuestionnaireInput,
  CreateQuestionnaireInput,
  CreateQuestionnaireQuestionInput,
  CreateQuestionnaireSectionInput,
  Questionnaire,
  QuestionnaireAuditEntry,
  QuestionnaireListItem,
  QuestionnaireQuestion,
  QuestionnaireSection,
  UpdateQuestionnaireInput,
  UpdateQuestionnaireQuestionInput,
  UpdateQuestionnaireSectionInput,
} from '@peles/shared';
import { apiClient } from './client';

export interface QuestionnaireListFilters {
  eventId?: string;
  isTemplate?: boolean;
  includeArchived?: boolean;
}

export async function listQuestionnaires(
  filters?: QuestionnaireListFilters,
): Promise<QuestionnaireListItem[]> {
  const params: Record<string, string> = {};
  if (filters?.eventId) params.eventId = filters.eventId;
  if (filters?.isTemplate !== undefined) params.isTemplate = String(filters.isTemplate);
  if (filters?.includeArchived) params.includeArchived = 'true';
  const { data } = await apiClient.get<QuestionnaireListItem[]>('/questionnaires', { params });
  return data;
}

export async function getQuestionnaire(id: string): Promise<Questionnaire> {
  const { data } = await apiClient.get<Questionnaire>(`/questionnaires/${id}`);
  return data;
}

export async function createQuestionnaire(input: CreateQuestionnaireInput): Promise<Questionnaire> {
  const { data } = await apiClient.post<Questionnaire>('/questionnaires', input);
  return data;
}

export async function updateQuestionnaire(
  id: string,
  input: UpdateQuestionnaireInput,
): Promise<Questionnaire> {
  const { data } = await apiClient.patch<Questionnaire>(`/questionnaires/${id}`, input);
  return data;
}

export async function deleteQuestionnaire(id: string): Promise<void> {
  await apiClient.delete(`/questionnaires/${id}`);
}

export async function cloneQuestionnaire(
  id: string,
  input: CloneQuestionnaireInput,
): Promise<Questionnaire> {
  const { data } = await apiClient.post<Questionnaire>(`/questionnaires/${id}/clone`, input);
  return data;
}

export async function saveQuestionnaireAsTemplate(
  id: string,
  templateName: string,
): Promise<Questionnaire> {
  const { data } = await apiClient.post<Questionnaire>(`/questionnaires/${id}/save-as-template`, {
    templateName,
  });
  return data;
}

export async function sendQuestionnaireLink(
  id: string,
  email?: string,
): Promise<Questionnaire & { fillUrl: string }> {
  const { data } = await apiClient.post<Questionnaire & { fillUrl: string }>(
    `/questionnaires/${id}/send-link`,
    { email },
  );
  return data;
}

export async function getQuestionnaireAudit(id: string): Promise<QuestionnaireAuditEntry[]> {
  const { data } = await apiClient.get<QuestionnaireAuditEntry[]>(`/questionnaires/${id}/audit`);
  return data;
}

export async function addQuestionnaireSection(
  questionnaireId: string,
  input: CreateQuestionnaireSectionInput,
): Promise<QuestionnaireSection> {
  const { data } = await apiClient.post<QuestionnaireSection>(
    `/questionnaires/${questionnaireId}/sections`,
    input,
  );
  return data;
}

export async function updateQuestionnaireSection(
  sectionId: string,
  input: UpdateQuestionnaireSectionInput,
): Promise<QuestionnaireSection> {
  const { data } = await apiClient.patch<QuestionnaireSection>(
    `/questionnaires/sections/${sectionId}`,
    input,
  );
  return data;
}

export async function deleteQuestionnaireSection(sectionId: string): Promise<void> {
  await apiClient.delete(`/questionnaires/sections/${sectionId}`);
}

export async function addQuestionnaireQuestion(
  sectionId: string,
  input: CreateQuestionnaireQuestionInput,
): Promise<QuestionnaireQuestion> {
  const { data } = await apiClient.post<QuestionnaireQuestion>(
    `/questionnaires/sections/${sectionId}/questions`,
    input,
  );
  return data;
}

export async function updateQuestionnaireQuestion(
  questionId: string,
  input: UpdateQuestionnaireQuestionInput,
): Promise<QuestionnaireQuestion> {
  const { data } = await apiClient.patch<QuestionnaireQuestion>(
    `/questionnaires/questions/${questionId}`,
    input,
  );
  return data;
}

export async function deleteQuestionnaireQuestion(questionId: string): Promise<void> {
  await apiClient.delete(`/questionnaires/questions/${questionId}`);
}

export async function applyQuestionnaireAnswer(questionId: string): Promise<{ applied: string }> {
  const { data } = await apiClient.post<{ applied: string }>(
    `/questionnaires/questions/${questionId}/apply`,
  );
  return data;
}
